import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, BehaviorSubject, finalize, of } from 'rxjs';
import { BaseService } from '../base.service';
import { AuthKeycloakService } from '../auth-keycloak.service';
import { IndexedDbService } from '../indexed-db.service';

export interface OccurrenceTemplateField {
  id: string;
  field_id: string;
  field_name: string;
  field_label: string;
  value_type: 'text' | 'number' | 'boolean' | 'date' | 'datetime' | 'select' | 'multiselect' | 'json' | 'textarea';
  required: boolean;
  options: string[] | null;
}

export interface OccurrenceTemplate {
  id: string;
  name: string;
  fields: OccurrenceTemplateField[];
}

export interface RecordTemplate {
  id: string;
  name: string;
}

export interface OccurrenceCreatePayload {
  network_segment_id: string;
  name: string;
  latitude: number;
  longitude: number;
  feature_id: number | null;
  field_values: { field_id: string; value: string | null }[];
  minimap_snapshot_base64?: string | null;
  polygon_coordinates?: number[][] | null;
  municipio?: string | null;
  estado?: string | null;
}

export interface OccurrenceUpdatePayload {
  name?: string;
  latitude?: number;
  longitude?: number;
  field_values?: { field_id: string; value: string | null }[];
}

export interface OccurrenceFieldValue {
  id: string;
  occurrence_id: string;
  field_id: string;
  value: string | null;
  field_name?: string;
  field_label?: string;
}

export interface UserBasic {
  id: string;
  name: string;
}

export interface Occurrence {
  id: string;
  name: string;
  network_segment_id: string;
  latitude: number;
  longitude: number;
  municipio?: string | null;
  estado?: string | null;
  source_properties?: Record<string, any> | null;
  segment_name?: string | null;
  network_name?: string | null;
  network_line_source_table?: string | null;
  network_polygon_source_table?: string | null;
  network_source_table?: string | null;
  feature_id: number | null;
  geometry: any;
  created_at: string;
  updated_at: string;
  created_by: UserBasic;
  occurrence_template?: OccurrenceTemplate;
  last_record_fields?: { last_record_date?: string; [key: string]: any };
  field_values?: OccurrenceFieldValue[];
  monitoring_tag?: {
    id: string;
    name: string;
    value: number;
    color: string;
  } | null;
}

export interface OccurrenceReportDraft {
  occurrence_id: string;
  data: Record<string, any>;
  updated_at: string;
}

export interface OccurrenceReportDraftPayload {
  data: Record<string, any>;
}

export interface PaginatedOccurrences {
  items: Occurrence[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
}

export interface OccurrenceStatusStats {
  invasao: number;
  notificado: number;
  vistoria_programada: number;
  segunda_vistoria: number;
  desocupado: number;
  total: number;
}

export interface ProjectOccurrenceConfig {
  project_id: string | null;
  occurrence_template_id: string | null;
  record_template_id?: string | null;
}

export interface OccurrenceHistory {
  id: string;
  occurrence_id: string;
  occurrence_record_id?: string | null;
  action: string;
  changes?: Record<string, any> | null;
  created_by?: string | null;
  created_at: string;
}

export interface OccurrenceLatestGeometry {
  occurrence_id: string;
  polygon_coordinates: number[][] | null;
  centroid: [number, number] | null;
  record_id: string | null;
  created_at: string | null;
  properties?: Record<string, any> | null;
}

export interface OccurrenceLatestGeometryFeatureCollection {
  type: 'FeatureCollection';
  features: any[];
}

export interface OccurrenceSearchColumn {
  field_name: string;
  field_label: string;
  source: 'occurrence' | 'template' | 'imported' | string;
}

export interface OccurrenceSearchColumnsResponse {
  columns: OccurrenceSearchColumn[];
}

export interface OccurrenceMatchResponse {
  segment_ids: string[];
  occurrence_ids: string[];
}

export interface OccurrenceImportRow {
  line: number;
  status: 'valid' | 'invalid';
  errors: string[];
  warnings?: string[];
  data: {
    malha: string;
    trecho: string;
    id: string;
    network_id?: string | null;
    network_segment_id?: string | null;
    feature_id?: number | null;
    latitude?: number | null;
    longitude?: number | null;
    existing_occurrence_id?: string | null;
    field_values?: { field_id: string; field_name: string; value: string }[];
  };
}

export interface OccurrenceImportPreview {
  summary: { total: number; valid: number; invalid: number; warnings?: number };
  rows: OccurrenceImportRow[];
}

export interface MonitoringTagApplyPayload {
  monitoring_tag_id: string | null;
  occurrence_ids?: string[];
  segment_ids?: string[];
  occurrence_filters?: Record<string, string> | null;
}

export interface MonitoringTag {
  id: string;
  name: string;
  value: number;
  color: string;
}

export interface MonitoringTagOption extends MonitoringTagApplyPayload {
  label: string;
}

export interface MonitoringChartItem {
  segment_id: string;
  segment_name: string;
  em_dia: number;
  vencendo: number;
  vencido: number;
}

export interface MonitoringChartResponse {
  items: MonitoringChartItem[];
}

export interface MonitoringTagDistributionItem {
  tag_id: string | null;
  tag_name: string;
  tag_color: string | null;
  count: number;
  percentage: number;
}

export interface MonitoringTagDistributionResponse {
  items: MonitoringTagDistributionItem[];
}

@Injectable({ providedIn: 'root' })
export class RailOccurrenceService extends BaseService {

  private activeOccurrenceRequests = 0;
  public hasPendingOccurrences$ = new BehaviorSubject<boolean>(false);

  private incrementOccurrenceCounter(): void {
    this.activeOccurrenceRequests++;
    this.hasPendingOccurrences$.next(true);
  }

  private decrementOccurrenceCounter(): void {
    this.activeOccurrenceRequests = Math.max(0, this.activeOccurrenceRequests - 1);
    this.hasPendingOccurrences$.next(this.activeOccurrenceRequests > 0);
  }

  constructor(
    private http: HttpClient,
    protected authKeycloakService: AuthKeycloakService,
    private indexedDbService: IndexedDbService
  ) {
    super(authKeycloakService);
  }

  getOccurrencesPaginated(
    segmentIds: string[],
    page = 1,
    pageSize = 10,
    lastRecordsFields?: string[],
    occurrenceFilters?: Record<string, string>,
    sortBy = 'created_at',
    sortDir: 'asc' | 'desc' = 'desc'
  ): Observable<PaginatedOccurrences> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('page_size', pageSize.toString())
      .set('sort_by', sortBy)
      .set('sort_dir', sortDir);

    segmentIds.forEach(id => {
      params = params.append('segment_ids', id);
    });

    if (lastRecordsFields && lastRecordsFields.length > 0) {
      lastRecordsFields.forEach(field => {
        params = params.append('last_records_fields', field);
      });
    }

    if (occurrenceFilters) {
      Object.entries(occurrenceFilters).forEach(([fieldName, value]) => {
        const normalizedFieldName = fieldName?.trim();
        const normalizedValue = value?.trim();

        if (!normalizedFieldName || !normalizedValue) {
          return;
        }

        params = params.append('filter_fields', normalizedFieldName);
        params = params.append('filter_values', normalizedValue);
      });
    }

    return this.http.get<PaginatedOccurrences>(
      `${this.url}api/rail/occurrences/paginated`,
      { params }
    );
  }

  getStatusStats(segmentIds: string[]): Observable<OccurrenceStatusStats> {
    let params = new HttpParams();
    segmentIds.forEach(id => {
      params = params.append('segment_ids', id);
    });
    return this.http.get<OccurrenceStatusStats>(
      `${this.url}api/rail/occurrences/stats`,
      { params }
    );
  }

  listOccurrenceTemplates(): Observable<OccurrenceTemplate[]> {
    return this.http.get<OccurrenceTemplate[]>(
      `${this.url}api/rail/occurrences/templates`
    );
  }

  listRecordTemplates(): Observable<RecordTemplate[]> {
    return this.http.get<RecordTemplate[]>(
      `${this.url}api/rail/occurrences/record-templates`
    );
  }

  createOccurrence(payload: OccurrenceCreatePayload): Observable<Occurrence> {
    return this.http.post<Occurrence>(
      `${this.url}api/rail/occurrences`,
      payload
    );
  }

  updateOccurrence(occurrenceId: string, payload: OccurrenceUpdatePayload): Observable<Occurrence> {
    return this.http.patch<Occurrence>(
      `${this.url}api/rail/occurrences/${occurrenceId}`,
      payload
    );
  }

  getProjectConfig(projectId: string): Observable<ProjectOccurrenceConfig> {
    return this.http.get<ProjectOccurrenceConfig>(
      `${this.url}api/rail/occurrences/project-config/${projectId}`
    );
  }

  updateProjectConfig(
    projectId: string,
    occurrenceTemplateId: string
  ): Observable<ProjectOccurrenceConfig> {
    return this.http.put<ProjectOccurrenceConfig>(
      `${this.url}api/rail/occurrences/project-config/${projectId}`,
      { occurrence_template_id: occurrenceTemplateId }
    );
  }

  getTemplateById(templateId: string): Observable<OccurrenceTemplate> {
    return this.http.get<OccurrenceTemplate>(
      `${this.url}api/rail/occurrences/templates/${templateId}`
    );
  }

  getNextOccurrenceName(projectId: string): Observable<{ name: string }> {
    return this.http.get<{ name: string }>(
      `${this.url}api/rail/occurrences/next-name/${projectId}`
    );
  }

  getOccurrenceById(occurrenceId: string): Observable<Occurrence> {
    return this.http.get<Occurrence>(
      `${this.url}api/rail/occurrences/detail/${occurrenceId}`
    );
  }

  getReportDraft(occurrenceId: string): Observable<OccurrenceReportDraft> {
    return this.http.get<OccurrenceReportDraft>(
      `${this.url}api/rail/occurrences/${occurrenceId}/report-draft`
    );
  }

  saveReportDraft(occurrenceId: string, payload: OccurrenceReportDraftPayload): Observable<OccurrenceReportDraft> {
    return this.http.put<OccurrenceReportDraft>(
      `${this.url}api/rail/occurrences/${occurrenceId}/report-draft`,
      payload
    );
  }

  getLatestGeometry(occurrenceId: string, recordId?: string): Observable<OccurrenceLatestGeometry> {
    const params = recordId ? `?record_id=${recordId}` : '';
    return this.http.get<OccurrenceLatestGeometry>(
      `${this.url}api/rail/occurrences/${occurrenceId}/latest-geometry${params}`
    );
  }

  getProjectLatestGeometries(projectId: string, format = 'geobuf'): Observable<any> {
    const cacheKey = `latest_geometries_${projectId}`;

    return new Observable<any>((observer) => {
      this.incrementOccurrenceCounter();
      let hasDecremented = false;

      const safeDecrement = () => {
        if (!hasDecremented) {
          hasDecremented = true;
          this.decrementOccurrenceCounter();
        }
      };

      this.indexedDbService.getLayer(cacheKey).then((cachedData) => {
        if (cachedData && cachedData.byteLength > 0) {
          console.log(`[OccurrenceService] Geometrias de ocorrências do projeto '${projectId}' carregadas do cache local (IndexedDB).`);
          observer.next(cachedData);
          safeDecrement();
          observer.complete();
        } else {
          let params = new HttpParams();
          params = params.set('format', format);

          this.http.get(`${this.url}api/rail/occurrences/projects/${projectId}/latest-geometries`, {
            params,
            responseType: 'arraybuffer'
          }).subscribe({
            next: (arrayBuffer: ArrayBuffer) => {
              if (arrayBuffer && arrayBuffer.byteLength > 0) {
                this.indexedDbService.saveLayer(cacheKey, arrayBuffer).then(() => {
                  return this.indexedDbService.saveLayerMeta(cacheKey, {
                    table_name: cacheKey,
                    use_demand: false,
                    size: arrayBuffer.byteLength,
                    last_loaded: Date.now()
                  });
                }).catch(err => {
                  console.warn('[OccurrenceService] Falha ao salvar geometrias de ocorrências no IndexedDB:', err);
                });
              }
              observer.next(arrayBuffer);
              safeDecrement();
              observer.complete();
            },
            error: (err) => {
              safeDecrement();
              observer.error(err);
            }
          });
        }
      }).catch((err) => {
        console.warn('[OccurrenceService] Falha ao consultar o IndexedDB, recorrendo à rede:', err);
        let params = new HttpParams();
        params = params.set('format', format);

        this.http.get(`${this.url}api/rail/occurrences/projects/${projectId}/latest-geometries`, {
          params,
          responseType: 'arraybuffer'
        }).subscribe({
          next: (arrayBuffer) => {
            observer.next(arrayBuffer);
            safeDecrement();
            observer.complete();
          },
          error: (netErr) => {
            safeDecrement();
            observer.error(netErr);
          }
        });
      });

      return () => {
        safeDecrement();
      };
    });
  }

  getProjectLatestCentroids(projectId: string, format = 'geobuf'): Observable<any> {
    const cacheKey = `latest_centroids_${projectId}`;

    return new Observable<any>((observer) => {
      this.incrementOccurrenceCounter();
      let hasDecremented = false;

      const safeDecrement = () => {
        if (!hasDecremented) {
          hasDecremented = true;
          this.decrementOccurrenceCounter();
        }
      };

      this.indexedDbService.getLayer(cacheKey).then((cachedData) => {
        if (cachedData && cachedData.byteLength > 0) {
          console.log(`[OccurrenceService] Centróides de ocorrências do projeto '${projectId}' carregados do cache local (IndexedDB).`);
          observer.next(cachedData);
          safeDecrement();
          observer.complete();
        } else {
          let params = new HttpParams();
          params = params.set('format', format);

          this.http.get(`${this.url}api/rail/occurrences/projects/${projectId}/latest-centroids`, {
            params,
            responseType: 'arraybuffer'
          }).subscribe({
            next: (arrayBuffer: ArrayBuffer) => {
              if (arrayBuffer && arrayBuffer.byteLength > 0) {
                this.indexedDbService.saveLayer(cacheKey, arrayBuffer).then(() => {
                  return this.indexedDbService.saveLayerMeta(cacheKey, {
                    table_name: cacheKey,
                    use_demand: false,
                    size: arrayBuffer.byteLength,
                    last_loaded: Date.now()
                  });
                }).catch(err => {
                  console.warn('[OccurrenceService] Falha ao salvar centróides de ocorrências no IndexedDB:', err);
                });
              }
              observer.next(arrayBuffer);
              safeDecrement();
              observer.complete();
            },
            error: (err) => {
              safeDecrement();
              observer.error(err);
            }
          });
        }
      }).catch((err) => {
        console.warn('[OccurrenceService] Falha ao consultar o IndexedDB, recorrendo à rede:', err);
        let params = new HttpParams();
        params = params.set('format', format);

        this.http.get(`${this.url}api/rail/occurrences/projects/${projectId}/latest-centroids`, {
          params,
          responseType: 'arraybuffer'
        }).subscribe({
          next: (arrayBuffer) => {
            observer.next(arrayBuffer);
            safeDecrement();
            observer.complete();
          },
          error: (netErr) => {
            safeDecrement();
            observer.error(netErr);
          }
        });
      });

      return () => {
        safeDecrement();
      };
    });
  }

  getProjectFilteredOccurrenceIds(
    projectId: string,
    segmentIds: string[] = [],
    filters: Record<string, string> = {}
  ): Observable<{ segment_ids: string[]; occurrence_ids: string[] }> {
    let params = new HttpParams();
    segmentIds.forEach((id) => { params = params.append('segment_ids', id); });
    Object.entries(filters).forEach(([key, val]) => {
      if (val && val.trim().length > 0) {
        params = params.append('filter_fields', key);
        params = params.append('filter_values', val);
      }
    });
    return this.http.get<{ segment_ids: string[]; occurrence_ids: string[] }>(
      `${this.url}api/rail/occurrences/projects/${projectId}/filter-ids`,
      { params }
    );
  }

  getProjectSearchColumns(projectId: string): Observable<OccurrenceSearchColumnsResponse> {
    return this.http.get<OccurrenceSearchColumnsResponse>(
      `${this.url}api/rail/occurrences/projects/${projectId}/search-columns`
    );
  }

  matchProjectOccurrences(
    projectId: string,
    fieldName: string,
    value: string,
    segmentIds: string[] = []
  ): Observable<OccurrenceMatchResponse> {
    let params = new HttpParams()
      .set('field_name', fieldName)
      .set('value', value);

    segmentIds.forEach((id) => {
      params = params.append('segment_ids', id);
    });

    return this.http.get<OccurrenceMatchResponse>(
      `${this.url}api/rail/occurrences/projects/${projectId}/match`,
      { params }
    );
  }

  getMonitoringTags(): Observable<MonitoringTag[]> {
    return this.http.get<MonitoringTag[]>(
      `${this.url}api/rail/occurrences/monitoring-tags`
    );
  }

getMonitoringTagsStats(
    networkIds: string[] = [],
    segmentIds: string[] = [],
    monitoringTagIds: string[] = [],
    periodDays: number | null = null
  ): Observable<OccurrenceStatusStats & { overdue: number; due_in_7_days: number; on_track: number; no_monitoring: number; }> {
    let params = new HttpParams();

    networkIds.forEach((id) => { params = params.append('network_ids', id); });
    segmentIds.forEach((id) => { params = params.append('segment_ids', id); });
    monitoringTagIds.forEach((id) => { params = params.append('monitoring_tag_ids', id); });
    if (periodDays) {
      params = params.set('period_days', periodDays.toString());
    }

    return this.http.get<OccurrenceStatusStats & { overdue: number; due_in_7_days: number; on_track: number; no_monitoring: number; }>(
      `${this.url}api/rail/occurrences/monitoring-tags-stats`,
      { params }
    );
  }

  getMonitoringOccurrences(
    networkIds: string[] = [],
    segmentIds: string[] = [],
    monitoringTagIds: string[] = [],
    page = 1,
    pageSize = 20,
    periodDays: number | null = null
  ): Observable<PaginatedOccurrences> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('page_size', pageSize.toString());

    networkIds.forEach((id) => { params = params.append('network_ids', id); });
    segmentIds.forEach((id) => { params = params.append('segment_ids', id); });
    monitoringTagIds.forEach((id) => { params = params.append('monitoring_tag_ids', id); });
    if (periodDays) {
      params = params.set('period_days', periodDays.toString());
    }

    return this.http.get<PaginatedOccurrences>(
      `${this.url}api/rail/occurrences/monitoring-occurrences`,
      { params }
    );
  }

  exportMonitoringOccurrences(payload: {
    network_ids: string[];
    segment_ids: string[];
    monitoring_tag_ids: string[];
    period_days?: number | null;
    format: 'csv' | 'xlsx';
  }): Observable<Blob> {
    return this.http.post(
      `${this.url}api/rail/occurrences/monitoring-occurrences/export`,
      payload,
      { responseType: 'blob' }
    );
  }

  applyMonitoringTags(payload: MonitoringTagApplyPayload): Observable<void> {
    return this.http.post<void>(
      `${this.url}api/rail/occurrences/monitoring-tags/apply`,
      payload
    );
  }

  getOccurrenceHistory(occurrenceId: string): Observable<OccurrenceHistory[]> {
    return this.http.get<OccurrenceHistory[]>(
      `${this.url}api/rail/occurrences/${occurrenceId}/history`
    );
  }

  exportGeo(
    segmentIds: string[],
    format: 'shp' | 'kml',
    occurrenceFilters?: Record<string, string>,
    lastRecordsFields?: string[],
  ): Observable<Blob> {
    const filters: Record<string, string> = {};
    if (occurrenceFilters) {
      Object.entries(occurrenceFilters).forEach(([k, v]) => {
        const nk = k?.trim();
        const nv = v?.trim();
        if (nk && nv) filters[nk] = nv;
      });
    }
    const body = {
      segment_ids: segmentIds,
      format,
      last_records_fields: lastRecordsFields ?? null,
      occurrence_filters: Object.keys(filters).length ? filters : null,
    };
    return this.http.post(
      `${this.url}api/rail/occurrences/export-geo`,
      body,
      { responseType: 'blob' }
    );
  }

  exportCsv(
    segmentIds: string[],
    occurrenceFilters?: Record<string, string>,
    lastRecordsFields?: string[],
  ): Observable<Blob> {
    const filters: Record<string, string> = {};
    if (occurrenceFilters) {
      Object.entries(occurrenceFilters).forEach(([k, v]) => {
        const nk = k?.trim();
        const nv = v?.trim();
        if (nk && nv) filters[nk] = nv;
      });
    }
    const body = {
      segment_ids: segmentIds,
      last_records_fields: lastRecordsFields ?? null,
      occurrence_filters: Object.keys(filters).length ? filters : null,
    };
    return this.http.post(
      `${this.url}api/rail/occurrences/export-csv`,
      body,
      { responseType: 'blob' }
    );
  }

  exportXlsx(
    segmentIds: string[],
    occurrenceFilters?: Record<string, string>,
    lastRecordsFields?: string[],
    options?: { projectName?: string; revisao?: string; dataInspecao?: string },
  ): Observable<Blob> {
    const filters: Record<string, string> = {};
    if (occurrenceFilters) {
      Object.entries(occurrenceFilters).forEach(([k, v]) => {
        const nk = k?.trim();
        const nv = v?.trim();
        if (nk && nv) filters[nk] = nv;
      });
    }
    const body = {
      segment_ids: segmentIds,
      last_records_fields: lastRecordsFields ?? null,
      occurrence_filters: Object.keys(filters).length ? filters : null,
      project_name: options?.projectName ?? null,
      revisao: options?.revisao ?? null,
      data_inspecao: options?.dataInspecao ?? null,
    };
    return this.http.post(
      `${this.url}api/rail/occurrences/export-xlsx`,
      body,
      { responseType: 'blob' }
    );
  }

  downloadImportTemplate(projectId: string, fmt: 'xlsx' | 'csv' = 'xlsx'): Observable<Blob> {
    return this.http.get(
      `${this.url}api/rail/occurrences/import/template`,
      { params: { project_id: projectId, fmt }, responseType: 'blob' }
    );
  }

  previewImport(projectId: string, file: File): Observable<OccurrenceImportPreview> {
    const fd = new FormData();
    fd.append('project_id', projectId);
    fd.append('file', file);
    return this.http.post<OccurrenceImportPreview>(
      `${this.url}api/rail/occurrences/import/preview`,
      fd
    );
  }

  commitImport(projectId: string, file: File): Observable<OccurrenceImportPreview & { created: number; overwritten?: number }> {
    const fd = new FormData();
    fd.append('project_id', projectId);
    fd.append('file', file);
    return this.http.post<OccurrenceImportPreview & { created: number; overwritten?: number }>(
      `${this.url}api/rail/occurrences/import/commit`,
      fd
    );
  }

  importGeoOccurrences(projectId: string, file: File): Observable<{ created: number; errors: string[] }> {
    const fd = new FormData();
    fd.append('project_id', projectId);
    fd.append('file', file);
    return this.http.post<{ created: number; errors: string[] }>(
      `${this.url}api/rail/occurrences/import-geo`,
      fd
    );
  }

  getMonitoringChart(
    networkIds: string[] = [],
    segmentIds: string[] = [],
    monitoringTagIds: string[] = [],
    periodDays: number | null = null
  ): Observable<MonitoringChartResponse> {
    let params = new HttpParams();

    networkIds.forEach((id) => { params = params.append('network_ids', id); });
    segmentIds.forEach((id) => { params = params.append('segment_ids', id); });
    monitoringTagIds.forEach((id) => { params = params.append('monitoring_tag_ids', id); });
    if (periodDays) {
      params = params.set('period_days', periodDays.toString());
    }

    return this.http.get<MonitoringChartResponse>(
      `${this.url}api/rail/occurrences/monitoring-chart`,
      { params }
    );
  }

  getMonitoringTagDistribution(
    networkIds: string[] = [],
    segmentIds: string[] = [],
    monitoringTagIds: string[] = [],
    periodDays: number | null = null
  ): Observable<MonitoringTagDistributionResponse> {
    let params = new HttpParams();

    networkIds.forEach((id) => { params = params.append('network_ids', id); });
    segmentIds.forEach((id) => { params = params.append('segment_ids', id); });
    monitoringTagIds.forEach((id) => { params = params.append('monitoring_tag_ids', id); });
    if (periodDays) {
      params = params.set('period_days', periodDays.toString());
    }

    return this.http.get<MonitoringTagDistributionResponse>(
      `${this.url}api/rail/occurrences/monitoring-tag-distribution`,
      { params }
    );
  }

  
  exportReportPdf(occurrenceId: string, payload: { data: Record<string, any> }): Observable<Blob> {
    return this.http.post(
      `${this.url}api/rail/occurrences/${occurrenceId}/report-pdf`,
      payload,
      { responseType: 'blob' }
    );
  }

  getSourceLayerData(tableName: string, mf40sysid: number): Observable<{ table_data: any[]; updated_at: string; total_pages: number }> {
    return this.http.post<{ table_data: any[]; updated_at: string; total_pages: number }>(
      `${this.url}api/forms/get-data/`,
      {
        table_name: tableName,
        limit: 1,
        page: 1,
        filters: [
          { column: 'mf40sysid', condition: '=', value: mf40sysid }
        ]
      }
    );
  }
}
